import React, { createContext, useContext, ReactNode, useRef } from "react"
import { useEffect, useState } from "react"
import { PageProps } from "gatsby"
import { useAuth, Inspector, getUser } from "./auth"
import { getFirestore, doc, serverTimestamp, setDoc } from "firebase/firestore"
import { useGetPreferences, set as setPreference } from "services/Preferences"
import { getDatabase, ref, onValue } from "firebase/database"
import { onDisconnect, set } from "firebase/database"
import ErrorBoundary from "@/components/ErrorBoundary"
import MESSAGE from "lang/messages"
import SEO from "@/components/seo"
import {
  onNotifications,
  notificationRead,
  type Notification,
  pushNotification,
} from "./Notification"
import { toast } from "@/components/toast"
import { addContributor, getInspection } from "@/database"
import { ChristmasScene } from "@/components/hodliday-theme/christmas"
import { ValentinesScene } from "@/components/hodliday-theme/valentines"
import { Haptics } from "@/services/Haptics"
const version = "11.0.0"

const Context = createContext<ContextType>({
  version,
  authenticating: true,
  inspector: null,
})
type GatsbyPageProps = Omit<PageProps, "children">
type Props = GatsbyPageProps & {
  children?: ReactNode
  "*"?: string
}
export type ContextType = {
  version: string
  authenticating: boolean
  inspector: Inspector | null
}
export function useAppContext(): ContextType {
  const context = useContext(Context)
  if (context === null) {
    throw new Error("useUser must be used within a UserProvider")
  }
  return context
}

export function Provider(props: Props) {
  const preferences = useGetPreferences()
  const inspector = useAuth(authenticated)
  const [authenticating, setAuthenticating] = useState(true)
  const [mounted, setMounted] = useState(false)
  const displayedNotifications = useRef(new Set<string>())

  useEffect(() => {
    setMounted(true)
    Haptics.init()
  }, [])

  function authenticated() {
    setAuthenticating(false)
  }
  function handleNotifications(notifications: Notification[]) {
    async function isContributionRequest(
      notification: Notification,
      status: "accepted" | "declined",
    ) {
      const inspector = await getUser()
      const { inspection: inspectionId, sender } = notification
      try {
        await notificationRead(notification.id, {})
        switch (status) {
          case "accepted": {
            const inspection = await getInspection(inspectionId)
            if (inspection) {
              const contributorUid =
                inspection.uid === inspector.uid ? sender : inspector.uid
              await addContributor(inspectionId, contributorUid)

              await pushNotification({
                sender: inspector.uid,
                receiver: sender,
                type: "contribution_request_accepted",
                inspection: inspectionId,
                username: `${inspector.fname} ${inspector.lname}`,
              })
            }
            break
          }
          case "declined":
            await pushNotification({
              sender: inspector.uid,
              receiver: sender,
              type: "contribution_request_declined",
              inspection: inspectionId,
              username: `${inspector.fname} ${inspector.lname}`,
            })
            break

          default:
            break
        }
      } catch (error: any) {
        console.error("Error handling contribution request:", error)
        toast({
          title: "Error",
          description:
            "There was a problem accepting this tag team request. Contact support.",
          duration: 5000,
        })
      }
    }
    for (let i = 0; i < notifications.length; i++) {
      const notification = notifications[i]
      const { id, type } = notification

      if (displayedNotifications.current.has(id)) continue
      displayedNotifications.current.add(id)

      // Trigger success haptic on unread notification
      Haptics.trigger("success")

      switch (type) {
        case "contribution_request": {
          const { username } = notification
          toast({
            title: "Tag Team!",
            description: `${username} would like to join your inspection. If you accept they will be able to makes changes to your content.`,
            duration: Infinity,
            accept: {
              label: "Accept",
              onClick: () => isContributionRequest(notification, "accepted"),
            },
            cancel: {
              label: "Decline",
              onClick: () => isContributionRequest(notification, "declined"),
            },
          })

          break
        }
        case "contributor_added": {
          const { username, inspection } = notification
          notificationRead(id, {})
          toast({
            title: "Tag Team!",
            description: `${username} added you as a contributor for inspection ${inspection}. This inspection will show up in your list of upcoming inspections.`,
            duration: Infinity,
          })

          break
        }
        case "contribution_request_accepted": {
          const { username } = notification
          notificationRead(id, {})
          toast({
            title: "Notice!",
            description: `${username} acccepted your request for a tag team inspection. 😎`,
            duration: Infinity,
          })
          break
        }
        case "contribution_request_declined": {
          const { username } = notification
          notificationRead(id, {})
          toast({
            title: "Notice!",
            description: `${username} rejected your request for a tag team inspection. 💔`,
            duration: Infinity,
          })
          break
        }
        default:
          break
      }
    }
  }
  useEffect(() => {
    if (!preferences) return
    document.documentElement.classList.toggle(
      "dark",
      preferences.theme === "dark" ||
        (!("theme" in preferences) &&
          window.matchMedia("(prefers-color-scheme: dark)").matches),
    )
  }, [preferences])
  useEffect(() => {
    window.onoffline = async event => {
      // setPreference({ offline_mode: true })
      console.log(MESSAGE.nextworkDisabled)
    }
    window.ononline = async event => {
      // setPreference({ offline_mode: false })
      console.log(MESSAGE.nextworkEnabled)
    }
  }, [])

  //handle client authentication redirect
  useEffect(() => {
    const isLoginPage = props.uri === "/login"

    if (!inspector && !authenticating && !isLoginPage) {
      window.location.assign("/login")
    }

    if (isLoginPage && inspector) {
      window.location.assign("/")
    }
  }, [authenticating, inspector, props.uri])

  useEffect(() => {
    if (!inspector && !authenticating) return localStorage.removeItem("uid")
    if (!inspector) return
    const database = getDatabase()
    const { uid, fname, lname, headshot } = inspector
    const document = doc(getFirestore(), `online-status/${uid}`)
    const status = ref(database, `/status/${uid}`)
    const connected = ref(database, ".info/connected")
    let unsubscribe = () => {}
    let notificationUnsubscribe = () => {}
    const isOffline = {
      state: "offline",
      last_online: serverTimestamp(),
    }
    const isOnline = {
      state: "online",
      last_online: serverTimestamp(),
      uid,
      name: `${fname} ${lname}`,
      headshot,
    }

    localStorage.setItem("uid", uid)

    unsubscribe = onValue(connected, snapshot => {
      if (snapshot.val() == false) return

      onDisconnect(status)
        .set(isOffline)
        .then(() => {
          set(status, isOnline)
          setDoc(document, isOnline, { merge: true })
        })
    })

    notificationUnsubscribe = onNotifications(handleNotifications)

    return () => {
      unsubscribe()
      notificationUnsubscribe()
    }
  }, [inspector])

  return (
    <Context.Provider value={{ version, inspector, authenticating }}>
      <ErrorBoundary>
        <SEO />

        {props.children}
        {mounted &&
          ((preferences?.theme && preferences.theme === "christmas") ||
            (new Date().getMonth() === 11 && new Date().getDate() <= 12)) && (
            <ChristmasScene />
          )}
        {mounted &&
          ((preferences?.theme && preferences.theme === "valentines") ||
            (new Date().getMonth() === 1 &&
              (new Date().getDate() === 14 ||
                new Date().getDate() === 15))) && <ValentinesScene />}
      </ErrorBoundary>
    </Context.Provider>
  )
}

export default Context
